import { usePageTitle } from 'utils/hooks/usePageTitle';
import {
  Container,
  InputWrapper,
  ListItem,
  ListOptions,
  NoContentLabel,
  NoContentLabelWrapper,
  RightSide,
  Title,
  TopSection,
} from './CustomModels.styled';
import { useTranslation } from 'react-i18next';
import Input from 'components/Input/Input';
import { useEffect, useState } from 'react';
import { useDebounce } from 'utils/hooks/useDebounce';
import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import Button from 'components/Button/Button';
import { MODELS_PER_PAGE, tableConfig } from './constants';
import { ICustomModelDTO, IModel } from 'types/Model.types';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import { useCreateCustomModel, useGetCustomModels } from './hooks';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import {
  useArchiveCustomModel,
  useRestoreCustomModel,
} from 'pages/SingleCustomModel/hooks';
import { CTAModal } from 'components/Modals/CTAModal/CTAModal';
import { useNavigateWithQuery } from 'utils/hooks/useNavigateWithQuery';
import Table from 'components/Table/Table';
import { formatCustomModelsTableData } from 'components/Table/tableDataFormatter';
import { AppMode } from 'store/Common/reducers/common';
import Loader from 'components/Loader/Loader';
import { resetConfigureForm } from 'store/Model/actions/model';

const CustomModels = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const [searchBy, setSearchBy] = useState<string>('');
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState<boolean>(false);
  const [archivedModelOption, setArchivedModelOption] =
    useState<boolean>(false);
  const debouncedSearchBy = useDebounce(searchBy);
  const [page, setPage] = useState<number>(1);
  const [archivedModelId, setArchivedModelId] = useState<number | null>(null);
  const [archiveModelName, setArchiveModelName] = useState<string>('');
  const [restoreModelId, setRestoreModelId] = useState<number | null>(null);
  const [restoreModelName, setRestoreModelName] = useState<string>('');
  const [isCTAModalOpen, setIsCTAModalOpen] = useState<boolean>(false);
  const [isShowingLoader, setIsShowingLoader] = useState<boolean>(false);

  useEffect(() => {
    dispatch(resetConfigureForm());
  }, []);

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  usePageTitle('Custom models');

  const { company } = useSelector((state: IRootReducerState) => state.userInfo);
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const { data: modelsData, isLoading: isLoadingModels } = useGetCustomModels(
    page,
    MODELS_PER_PAGE,
    debouncedSearchBy,
    company.id,
    appMode,
    archivedModelOption
  );

  let tableData: ICustomModelDTO[] = [];
  if (modelsData?.models) {
    tableData = formatCustomModelsTableData(
      modelsData?.models
        .map((model: IModel[]) => {
          return model;
        })
        .flat()
    ).map((model) => ({
      ...model,
      name: model.name || t('Unnamed model'),
    }));
  }

  const { mutate: createCustomModel } = useCreateCustomModel(company?.id);
  const { mutate: archiveCustomModel } = useArchiveCustomModel();
  const { mutate: restoreCustomModel } = useRestoreCustomModel();

  const onArchiveClick = (rowData: any) => {
    const { id, name } = rowData;
    setIsArchiveModalOpen(true);
    setArchiveModelName(name);
    setArchivedModelId(id);
  };
  const onViewClick = (rowData: any) => {
    const { id } = rowData;
    navigate(`${id}`);
  };

  const onRestoreClick = (rowData: any) => {
    const { id, name } = rowData;
    setIsCTAModalOpen(true);
    setRestoreModelName(name);
    setRestoreModelId(id);
  };

  return (
    <Container>
      {isShowingLoader && <Loader />}
      <TopSection>
        <Title>{t('Custom models')}</Title>
        <RightSide>
          <InputWrapper>
            <Input
              placeholder={t('Search models')}
              width={'100%'}
              onChange={(e: any) => setSearchBy(e.target.value)}
              icon={Search}
              wrapperStyles={{ width: '100%' }}
            />
          </InputWrapper>
          <Button
            label={t('Create new model')}
            primary
            width="150rem"
            disabled={isShowingLoader}
            onClick={() => {
              setIsShowingLoader(true);
              createCustomModel(undefined, {
                onSettled: () => setIsShowingLoader(false),
              });
            }}
          />
        </RightSide>
      </TopSection>

      <ListOptions>
        <ListItem
          selectedOption={!archivedModelOption}
          onClick={() => {
            setArchivedModelOption(false);
            setPage(1);
          }}
        >
          {t('Available')}
        </ListItem>
        <ListItem
          selectedOption={archivedModelOption}
          onClick={() => {
            setArchivedModelOption(true);
            setPage(1);
          }}
        >
          {t('Archived')}
        </ListItem>
      </ListOptions>

      {tableData.length ? (
        <Table
          page={page}
          perPage={MODELS_PER_PAGE}
          total={modelsData?.pagination?.total}
          isLoading={isLoadingModels}
          tableData={tableData}
          tableConfig={tableConfig}
          hasExtraAction={true}
          loadPage={(newPage: number) => setPage(newPage)}
          onViewButtonClick={onViewClick}
          onArchive={onArchiveClick}
          onRestore={archivedModelOption ? onRestoreClick : undefined}
        />
      ) : (
        <NoContentLabelWrapper>
          <NoContentLabel>{t('No models found')}</NoContentLabel>
        </NoContentLabelWrapper>
      )}

      <DangerModal
        isOpen={isArchiveModalOpen}
        setIsOpen={setIsArchiveModalOpen}
        title={t('Archive model')}
        submit={{
          onClick: () => {
            if (archivedModelId) archiveCustomModel({ id: archivedModelId });
            setIsArchiveModalOpen(false);
          },
          text: t('Archive'),
          disabled: false,
        }}
        cancel={{
          onClick: () => {
            setIsArchiveModalOpen(false);
          },
        }}
        description={`
          ${t('Are you sure you want to archive')}
        ${archiveModelName}?`}
      />

      <CTAModal
        isOpen={isCTAModalOpen}
        setIsOpen={setIsCTAModalOpen}
        firstButtonClick={() => {
          setIsCTAModalOpen(false);
        }}
        firstButtonText={t('Cancel')}
        secondButtonClick={() => {
          if (restoreModelId) restoreCustomModel({ id: restoreModelId });
          setIsCTAModalOpen(false);
        }}
        secondButtonText={t('Confirm')}
        title={t('Restore model')}
        description={`
          ${t('Are you sure you want to restore')}
        ${restoreModelName}?`}
      />
    </Container>
  );
};

export default CustomModels;
